import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertTriangle, Mail, Play } from "lucide-react";
import { SettingsHeader, SettingsSaveBar, parseList } from "./_shared";

export default function AlertsSettings() {
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [pu, setPu] = useState("");
  const [mi, setMi] = useState("");
  const [email, setEmail] = useState(true);
  const [reportEnabled, setReportEnabled] = useState(false);
  const [reportDay, setReportDay] = useState(5);
  const [reportRecipients, setReportRecipients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [runningReport, setRunningReport] = useState(false);

  const load = async () => {
    const [r, u] = await Promise.all([
      api.get("/settings"),
      api.get("/users").catch(() => ({ data: [] })),
    ]);
    setS(r.data);
    setUsers(u.data);
    setPu((r.data.per_user_thresholds || []).join(", "));
    setMi((r.data.mission_thresholds || []).join(", "));
    setEmail(!!r.data.email_alerts_enabled);
    setReportEnabled(!!r.data.monthly_report_enabled);
    setReportDay(r.data.monthly_report_day || 5);
    setReportRecipients(r.data.monthly_report_recipients || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        per_user_thresholds: parseList(pu),
        mission_thresholds: parseList(mi),
        email_alerts_enabled: email,
        monthly_report_enabled: reportEnabled,
        monthly_report_day: reportDay,
        monthly_report_recipients: reportRecipients,
      });
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const toggleRecipient = (uid) => {
    setReportRecipients((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const runReportNow = async () => {
    setRunningReport(true);
    try {
      const r = await api.post("/admin/monthly-report/run");
      const sent = r.data.sent?.length || 0;
      const failed = r.data.failed?.length || 0;
      if (sent > 0) toast.success(`Report sent to ${sent} recipient(s)`);
      else if (failed > 0) toast.warning(`Report generated; email send failed for ${failed} recipient(s).`);
      else toast.info("Report generated. No recipient configured (CEO fallback).");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setRunningReport(false); }
  };

  if (!s) return <div className="p-12 text-zinc-500">Loading…</div>;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[900px]" data-testid="settings-page-alerts">
      <SettingsHeader
        kicker="Alertes"
        title="Alerts & monthly report"
        description="Configure MD thresholds and the automated CEO report."
      />

      {/* JH thresholds */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-8 mb-6">
        <div className="flex items-center gap-3 text-zinc-900">
          <AlertTriangle size={18} className="text-amber-500" />
          <h2 className="font-heading font-bold text-lg">MD alert thresholds</h2>
        </div>

        <div>
          <Label className="mb-2 block">Per-collaborator thresholds (% of allocated MD)</Label>
          <Input data-testid="settings-per-user" value={pu} onChange={(e) => setPu(e.target.value)} placeholder="80, 100" />
          <p className="text-xs text-zinc-500 mt-1">Comma-separated integers, 1-500.</p>
        </div>

        <div>
          <Label className="mb-2 block">Mission thresholds (% of mission budget)</Label>
          <Input data-testid="settings-mission" value={mi} onChange={(e) => setMi(e.target.value)} placeholder="80, 100" />
          <p className="text-xs text-zinc-500 mt-1">Notifies the mission manager and CEOs.</p>
        </div>

        <div className="flex items-center justify-between border-t border-zinc-100 pt-6">
          <div>
            <div className="text-sm font-medium">Send alerts by email (Gmail)</div>
            <div className="text-xs text-zinc-500 mt-1">The mission manager is the sender. In-app notifications remain active.</div>
          </div>
          <Switch data-testid="settings-email-toggle" checked={email} onCheckedChange={setEmail} />
        </div>
      </div>

      {/* Monthly CEO report */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-6 mb-6">
        <div className="flex items-center gap-3 text-zinc-900">
          <Mail size={18} className="text-blue-600" />
          <h2 className="font-heading font-bold text-lg">Monthly CEO report</h2>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl">Automatically generates a PDF on the configured day at 08:00 UTC and emails it via Gmail to selected recipients. Covers active missions, MD consumed by client, top AR debtors and last month's alerts.</p>

        <div className="flex items-center justify-between border-t border-zinc-100 pt-6">
          <div>
            <div className="text-sm font-medium">Enable automatic monthly report</div>
            <div className="text-xs text-zinc-500 mt-1">One run per month on the configured day (UTC).</div>
          </div>
          <Switch data-testid="settings-report-toggle" checked={reportEnabled} onCheckedChange={setReportEnabled} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="mb-2 block">Day of month (1-28)</Label>
            <Input type="number" min={1} max={28} value={reportDay} onChange={(e) => setReportDay(parseInt(e.target.value, 10) || 1)} data-testid="settings-report-day" />
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Recipients</Label>
          <p className="text-xs text-zinc-500 mb-3">Select the users who will receive the PDF by email. If none, fall back to all CEO accounts.</p>
          <div className="border border-zinc-200 rounded-md p-3 max-h-64 overflow-y-auto space-y-1">
            {users.filter(u => u.status === "ACTIVE").map((u) => {
              const checked = reportRecipients.includes(u.user_id);
              return (
                <label key={u.user_id} className="flex items-center justify-between py-1.5 px-2 hover:bg-zinc-50 rounded cursor-pointer" data-testid={`report-recipient-${u.user_id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <input type="checkbox" checked={checked} onChange={() => toggleRecipient(u.user_id)} />
                    <div className="min-w-0">
                      <div className="text-sm truncate">{u.name}</div>
                      <div className="text-[10px] font-mono text-zinc-500 uppercase">{u.role} · {u.email}</div>
                    </div>
                  </div>
                </label>
              );
            })}
            {users.length === 0 && <div className="text-sm text-zinc-500 text-center py-4">No users.</div>}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
          <button onClick={runReportNow} disabled={runningReport} data-testid="settings-report-run" className="inline-flex items-center gap-2 px-4 h-10 border border-zinc-300 text-sm rounded-md hover:bg-zinc-50 disabled:opacity-50">
            <Play size={13} /> {runningReport ? "Running…" : "Run now (test)"}
          </button>
          <span className="text-xs text-zinc-500">Useful to verify the flow before scheduling.</span>
        </div>
      </div>

      <SettingsSaveBar onSave={save} onReset={load} saving={saving} testId="settings-save" />
    </div>
  );
}
